import type { Translations } from './ru';

export const en: Translations = {
  header: {
    nav: {
      howItWorks: 'How it works',
      example:    'Sample report',
      faq:        'FAQ',
    },
    cta: 'Get my plan',
  },

  footer: {
    tagline: 'We read the labels on your skincare and build a routine that fits your skin — without unnecessary actives or extra spend.',
    nav:     'Navigation',
    contact: 'Contact',
    privacy: 'Privacy',
    copyright: '© {year} SkinBloom · Personal skincare analysis',
    disclaimer: 'Not a substitute for a dermatologist',
    links: {
      howItWorks: 'How it works',
      example:    'Sample report',
      whyBetter:  'Why us',
      faq:        'FAQ',
    },
  },

  hero: {
    badge: 'Personal skincare · $4.99 one-time · 100% refund',
    headline: ['A skincare plan', 'made for your skin —', 'using what you already have.'],
    subhead:  'Show us your bottles — we’ll work out what to keep, what to swap and what’s missing. Plan in your inbox in 5 minutes.',
    ctaPrimary:   'Get my plan',
    ctaSecondary: 'See sample',
    trustCount:   '12,000+ plans created',
    badges: {
      private: 'Confidential',
      email:   'By e-mail',
      time:    '5 minutes',
    },
    cards: {
      skinType: {
        label: 'SKIN TYPE',
        value: 'Combination,\nsensitive',
      },
      actives: {
        label: 'ACTIVES',
        items: ['Niacinamide', 'Ceramides', 'Hyaluronic acid'],
      },
      plan: {
        label: 'CARE PLAN',
        text:  'AM · PM · 5 steps',
      },
      report: {
        title:  'Building report',
        status: 'Almost done!',
      },
    },
  },

  problem: {
    titleA: 'Plenty of products.',
    titleB: 'Not enough clarity.',
    cards: [
      { title: 'Buying by reviews',                 text: 'But no one told you whether it actually fits your skin.' },
      { title: 'Ingredient lists feel foreign',     text: 'Acids, retinol, ceramides, niacinamide. What pairs with what — unclear.' },
      { title: 'The shelf grows, the system doesn’t', text: 'Mornings at the mirror you still don’t know what to put on first or why.' },
    ],
  },

  preview: {
    eyebrow:  'Sample report',
    titleA:   'Here’s what you’ll get',
    titleB:   'after the analysis',
    subtitle: 'Not generic advice — a concrete plan: what to keep, what to drop, what to add and how to use it.',
    moreNote: 'Your full report includes 5 more sections: a detailed routine, stage-by-stage breakdown, expected results and product recommendations.',
    ctaPrimary: 'I want a plan like this',
    ctaOverlay: 'Open the full sample report',

    swaps: {
      title: 'Analysis of your products',
      rows: [
        { from: 'TO Niacinamide 10% + Zinc',  to: 'G&G Stress Less',          note: '10% is too strong for sensitive skin' },
        { from: 'CeraVe Hydrating Cleanser',  to: 'CeraVe Hydrating Cleanser',  note: 'Good fit — keep it' },
        { from: null,                          to: 'Balea Panthenol Serum',     note: 'Needed to repair the skin barrier' },
      ],
      missing: 'Not in your routine',
    },

    ingredients: {
      title: 'Ingredients for your skin',
      items: [
        { name: 'Niacinamide 2–5%',        reason: 'Reduces redness, strengthens the barrier' },
        { name: 'Ceramides + cholesterol', reason: 'Rebuild the protective layer of the skin' },
        { name: 'Panthenol (B5)',          reason: 'Calms irritation, restores comfort' },
        { name: 'Hyaluronic acid',         reason: 'Retains moisture — fights tightness' },
      ],
      caption: 'Concentrations and pairings tuned for sensitive skin with redness.',
    },

    routine: {
      title: 'Care plan',
      tabs:  { morning: 'Morning', evening: 'Evening' },
      steps: {
        morning: ['Cleanse', 'Niacinamide', 'Moisturise', 'SPF'],
        evening: ['Cleanse', 'Panthenol', 'Cream', 'Repair'],
      },
    },

    timeline: {
      title: 'When you’ll see results',
      steps: [
        { period: '1–2 wks', text: 'Less tightness, skin looks calmer' },
        { period: '4 wks',   text: 'Redness less visible, skin more even' },
        { period: '6–8 wks', text: 'Barrier rebuilt, condition stable' },
      ],
    },
  },

  status: {
    ok:      'Good fit',
    replace: 'Replace',
    missing: 'None',
  },

  howItWorks: {
    titleA: 'How chaos becomes',
    titleB: 'a clear plan',
    flow:   ['Your products', 'Ingredient analysis', 'Care plan'],
    steps: [
      { n: '01', title: 'Tell us about your skin and bottles', text: 'Skin type, concerns, goals, and what’s already on your shelf. Type it out or send a photo.' },
      { n: '02', title: 'We read every label',                  text: 'We see what fits your skin, what overlaps, what irritates and what’s missing.' },
      { n: '03', title: 'You get a step-by-step plan',          text: 'What to keep, drop, add — and exactly how to use each product, morning and night.' },
    ],
  },

  whyBetter: {
    titleA: 'Not another generic article.',
    titleB: 'A plan made for your skin.',
    cards: [
      { title: 'Not driven by trends',                 text: 'We look at your skin and the formula — not at the popularity of a product.' },
      { title: 'No need to repurchase everything',     text: 'First we check what in your routine you can simply keep using.' },
      { title: 'No unnecessary actives',                text: 'We show what you really need and what could just irritate.' },
      { title: 'Clear instructions',                    text: 'Morning, evening, frequency — concrete, no guesswork.' },
    ],
    tagline: 'SkinBloom won’t sell you a fresh shelf. We help you see what already works in your routine — and what is genuinely missing.',
  },

  trust: {
    titleA: 'Honest, plain,',
    titleB: 'with no over-promising',
    cards: [
      { title: 'Not a replacement for a dermatologist', text: 'If you have a skin condition, please see a doctor.' },
      { title: 'Not a push to repurchase everything',   text: 'We start by analysing what you already own.' },
      { title: 'Focus on the formula',                  text: 'Actives, concentrations, compatibility and possible irritants.' },
      { title: 'Your data stays private',               text: 'It’s used only to build your report.' },
    ],
  },

  planSection: {
    titleA:   'Let’s start with your skin',
    subtitle: 'A few questions — and we’ll send your finished plan to your e-mail in a couple of minutes.',
  },

  faq: {
    title: 'Frequently asked',
    items: [
      { q: 'Is this a replacement for a dermatologist?',
        a: 'No. SkinBloom is a tool for analysing formulas and building a personal routine. If you have a skin condition (acne, rosacea, eczema), please see a dermatologist.' },
      { q: 'Do I need to buy new products?',
        a: 'Not necessarily. We first analyse what you already own and show what can stay. Replacements are recommended only where a product genuinely doesn’t fit.' },
      { q: 'Can I include products I already have at home?',
        a: 'Yes — that’s exactly how the analysis works. You list what you currently use, and we explain what fits your skin, what to swap and what’s missing.' },
      { q: 'What’s in the report?',
        a: 'A personal skin profile, a morning and evening routine, an actives compatibility check, an ingredient list — what to use and avoid — and concrete product picks.' },
      { q: 'When will I get the plan?',
        a: 'The report is built from your answers and is usually ready right after you finish the form. We send the finished report by e-mail.' },
      { q: 'What if I don’t know my skin type?',
        a: 'No problem. The form has hints and follow-up questions to identify the type from symptoms. Pick "Don’t know" and we’ll figure it out.' },
      { q: 'Does it work for sensitive skin?',
        a: 'Especially well. With sensitive skin it matters which actives suit you and which can irritate. The analysis is built to avoid the latter and pick gentle yet effective options.' },
    ],
  },

  finalCta: {
    eyebrow: '5 minutes — and the plan is yours',
    titleA:  'Time to get clear',
    titleB:  'about your shelf',
    body:    '5 minutes — and a plan for your skin lands in your inbox.\nNot internet advice, but concrete steps for what you already own.',
    cta:     'Get my plan',
    trust:   '12,000+ plans created',
  },

  form: {
    pageH1A:    'Let’s build',
    pageH1B:    'your personal routine',
    pageSub:    'Tell us a little about yourself, and we’ll start preparing.',
    progress:   'Progress',
    stepLabel:  'Step {n} of {total}',
    back:       'Back',
    next:       'Next',
    submit:     'Build my plan — {price}',
    fineprint:  'Confidential · Plan delivered by e-mail · 100% refund if it doesn’t fit',

    skinTypes: {
      dry: 'Dry', oily: 'Oily', combo: 'Combination',
      sensitive: 'Sensitive', unknown: 'Don’t know',
    },

    skin: {
      eyebrowSkinType: 'Skin type: {type}',
      title:           'Tell us a little about your skin',
      subtitle:        'Type, condition, what you like and dislike — the more detail, the more accurate the plan.',
      placeholder:     'For example: skin is dry, tight in the morning, sometimes redness shows up on the cheeks.',
      hint:            'A couple of words is enough. The more details, the better.',
    },

    allergies: {
      title:        'Any allergies or restrictions?',
      subtitle:     'So we can rule them out of your plan precisely.',
      yes:          'Yes, I do',
      no:           'No, I’m good',
      labelDetails: 'What are you allergic to?',
      placeholder:  'For example: vitamin C, essential oils, nuts...',
      okMessage:    'Great — skipping this step.',
    },

    products: {
      title:           'Tell us about your current products?',
      subtitle:        'Type them out, drop a photo, or leave it empty.',
      labelText:       'List your products',
      placeholderText: 'For example: CeraVe cleanser, La Roche-Posay cream, vitamin C serum...',
      orDivider:       'and / or',
      labelPhoto:      'Upload a photo',
      dropzonePrompt:  'Drag a photo here or pick a file',
      removeFile:      'Remove file',
      hint:            'A single shot of the bottles works — just make sure the labels are readable.',
    },

    contact: {
      title:       'Where should we send the plan?',
      subtitle:    'E-mail — we’ll send the report in a few minutes.',
      placeholder: 'example@gmail.com',
      hint:        'Used only to send your report. No spam.',
    },

    loading: {
      title:    'Analysing your skin profile…',
      subtitle: 'This will only take a moment.',
    },

    limit: {
      titleA:   'We’ve hit the limit',
      titleB:   'on new requests',
      body:     'Our algorithm is temporarily not accepting new requests due to high load. We’ll be back soon — check in later 🧡',
      cta:      'Back to home',
      footer:   'We’ll announce on the site as soon as a slot opens for new plans.',
    },
  },

  example: {
    backHome: 'Back to home',
    eyebrow:  'Personal report',
    title:    'SkinBloom Sample Report',
    subtitle: 'Built from the analysis of your skin and your answers to the form.',
    meta: {
      date:   'Created on',
      dateValue: '24 May 2024',
      id:     'Report ID',
      idValue: 'SB-240524-001',
    },
    score: {
      label:    'Overall skin index',
      good:     'Excellent',
      heading:  'Your skin is in good shape',
      body:     'Keep up your current routine and pay attention to the improvement areas.',
      scale:    ['Low', 'Medium', 'Excellent'],
    },
    sections: {
      analysis:    'Analysis of your skin',
      recommendations: 'Formula and product recommendations',
      detailedPlan:    'Detailed care plan',
      stages:          'Products by stage',
      forecast:        'Expected changes',
    },
    profile: {
      title: 'Skin profile',
      rows: [
        { label: 'Skin type',     value: 'Dry' },
        { label: 'Sensitivity',   value: 'High' },
        { label: 'Redness',       value: 'On the cheeks' },
        { label: 'Tightness',     value: 'After cleansing' },
        { label: 'Reactivity',    value: 'To actives' },
      ],
    },
    zones: {
      title: 'Improvement areas',
      sub:   'What the plan targets',
      items: ['Redness', 'Tightness', 'Skin barrier', 'Sensitivity'],
      tipLabel: 'Tip:',
      tipBody:  'a gentle cleanser and a barrier-repair cream help with all areas at once.',
    },
    carePlan: {
      title:   'Care plan',
      sub:     'Morning · Evening',
      steps:   ['Cleanse', 'Niacinamide', 'Moisturise', 'SPF'],
      cta:     'See full plan',
    },
    ingredients: {
      title: 'Key ingredients',
      items: [
        { name: 'Niacinamide (2–5%)',  note: 'Reduces redness, strengthens the barrier' },
        { name: 'Ceramides',            note: 'Rebuild the protective layer' },
        { name: 'Hyaluronic acid',      note: 'Hydrates, reduces tightness' },
        { name: 'Panthenol (B5)',       note: 'Calms irritation' },
      ],
      extra: '+ Zinc gluconate — for occasional breakouts',
    },
    productRecs: {
      title: 'Needs replacing',
      swaps: [
        { from: 'TO Niacinamide 10% + Zinc',   to: 'Geek & Gorgeous Stress Less' },
        { from: 'La Roche-Posay Effaclar H',   to: 'LRP Cicaplast B5 Light' },
        { from: null,                           to: 'Balea Panthenol Serum' },
        { from: null,                           to: 'TO Ascorbyl Glucoside' },
      ],
    },
    tips: {
      title: 'Tips and recommendations',
      items: [
        'Wear SPF every day, even in winter',
        'Don’t skip moisturiser — the barrier needs care',
        'Avoid niacinamide 10%+ on sensitive skin',
        'Introduce new products one at a time, every two weeks',
        'A gentle vitamin C — mornings, three times a week',
      ],
    },
    detailed: {
      eyebrow: 'Step by step',
      heading: 'Your final care plan',
      tabs: { morning: 'Morning', evening: 'Evening', weekly: 'By week' },
      stepTitles: {
        cleanse:     'Cleansing',
        niacin:      'Niacinamide 2-5%',
        moisturise:  'Moisturise',
        spf:         'SPF',
        panthenol:   'Panthenol (2-5%)',
        creamRepair: 'Restorative Cream',
        vitaminC:    'Mon/Wed/Fri - Vitamin C',
      },
      productNotes: {
        niacin10Strong:  'TO Niacinamide 10% - too strong',
        effaclarMorning: 'Effaclar H - not suitable',
        effaclarEvening: 'Effaclar H - insufficient',
      },
    },
    stage: {
      shouldHave: 'Should have',
      avoid:      'Avoid',
      yourProducts: 'Your products',
      examples:     'Example products',
      noProduct:    'No suitable product',
      stages: [
        {
          title: 'Cleansing (morning and evening)',
          shouldHave: ['Mild surfactants', 'No alcohols or fragrance', 'Ceramides / glycerin / amino acids'],
          avoid: ['Acidic toners', 'Harsh gels for problematic skin', 'Heavily foaming formulas'],
          yourProducts: [{ name: 'CeraVe Hydrating Cleanser', ok: true }, { name: 'Pixi Glow Tonic', ok: false }],
          examples: [
            { tier: 'Budget',   items: ['CeraVe Hydrating Cleanser', 'Simple Refreshing Facial Wash'] },
            { tier: 'Mid-range', items: ['LRP Toleriane Dermo Cleanser', 'Bioderma Sensibio Foaming Gel'] },
          ],
        },
        {
          title: 'Niacinamide (morning)',
          shouldHave: ['2–5% concentration', 'Little or no zinc', 'Soothing additions'],
          avoid: ['10%+ concentrations', 'Combos with acids', 'Stinging formulas'],
          yourProducts: [{ name: 'TO Niacinamide 10% + Zinc', ok: false }],
          examples: [
            { tier: 'Budget',   items: ['TO Niacinamide 5%', 'Revolution 2% Niacinamide'] },
            { tier: 'Mid-range', items: ['G&G Stress Less', 'LRP Mela B3'] },
          ],
        },
        {
          title: 'Moisturising (morning and evening)',
          shouldHave: ['Ceramides', 'Cholesterol', 'Fatty acids', 'Light texture'],
          avoid: ['Oily-skin formulas', 'Alcohols', 'Acidic ingredients'],
          yourProducts: [{ name: 'La Roche-Posay Effaclar H', ok: false }],
          examples: [
            { tier: 'Budget',   items: ['CeraVe Moisturising Cream', 'Balea Cica Cream'] },
            { tier: 'Mid-range', items: ['LRP Cicaplast B5 Light', 'Bioderma Atoderm Gel-Crème'] },
          ],
        },
        {
          title: 'SPF (morning)',
          shouldHave: ['SPF 50', 'Light texture', 'Fragrance-free', 'Suits sensitive skin'],
          avoid: [],
          yourProducts: [{ name: 'Garnier Ambre Solaire Fluid SPF50', ok: true }],
          examples: [
            { tier: 'Mid-range', items: ['LRP UVMune 400'] },
            { tier: 'Premium',   items: ['SkinCeuticals Ultra Facial Defense'] },
          ],
        },
        {
          title: 'Panthenol (evening)',
          shouldHave: ['2–5% panthenol', 'No alcohols', 'No acids'],
          avoid: [],
          yourProducts: [],
          examples: [
            { tier: 'Budget',   items: ['Balea Panthenol Serum'] },
            { tier: 'Mid-range', items: ['LRP Cicaplast B5 Serum'] },
          ],
        },
        {
          title: 'Vitamin C (some mornings)',
          shouldHave: ['Stable forms (SAP/MAP/AG)', '5–10% concentration', 'No irritating add-ons'],
          avoid: [],
          yourProducts: [],
          examples: [
            { tier: 'Budget',   items: ['TO Ascorbyl Glucoside 12%'] },
            { tier: 'Mid-range', items: ['G&G C-Glow'] },
          ],
        },
      ],
    },
    timeline: {
      eyebrow: 'If you stick with the plan',
      heading: 'Expected changes',
      steps: [
        { period: '1–2 weeks', num: '01', items: ['Less tightness', 'Skin looks calmer'] },
        { period: '4 weeks',   num: '02', items: ['Redness less visible', 'Skin softer and more even'] },
        { period: '6–8 weeks', num: '03', items: ['Barrier rebuilt', 'Sensitivity reduced', 'Stable skin condition'] },
      ],
    },
    finalCta: {
      eyebrow: 'Personal plan',
      heading: 'Want a personal plan of your own?',
      body:    'Above is a sample report. Yours will be tailored to your skin and your products.',
      cta:     'Get my care plan',
    },
    confidential: 'The report is private — only you can see it',
  },
};
